/**
 * Step sub-components and shared helpers for HrLeaveBalanceImportPage.
 *
 * Kept in a sub-module so the main page file stays under 500 lines.
 */

import { useRef, useState } from 'react'
import { VISUALLY_HIDDEN } from '../../lib/visually-hidden'
import {
  parseLeaveCsv,
  importLeaveBalances,
  LEAVE_IMPORT_TEMPLATE_CSV,
  LEAVE_IMPORT_ENTITLEMENT_ONLY_TEMPLATE_CSV,
  type LeaveImportResult,
  type LeaveImportRow,
} from '../../api/hr-leave-import'
import { LeaveImportPreviewTable } from './LeaveImportPreviewTable'

// ── Shared style helpers ──────────────────────────────────────────────────────

export function primaryBtn(disabled = false): React.CSSProperties {
  return {
    padding: '8px 16px',
    background: disabled ? 'var(--surface-2)' : 'var(--steel-blue)',
    color: disabled ? 'var(--text-3)' : '#fff',
    border: 'none',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 14,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'opacity 100ms',
  }
}

export function secondaryBtn(): React.CSSProperties {
  return {
    padding: '8px 14px',
    background: 'var(--surface)',
    color: 'var(--text-2)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  }
}

function alertBox(variant: 'danger' | 'warning' | 'info'): React.CSSProperties {
  const map = {
    danger: {
      background: 'var(--danger-bg)',
      color: 'var(--danger-fg)',
      border: '1px solid var(--danger-line)',
    },
    warning: {
      background: 'var(--warning-bg, var(--danger-bg))',
      color: 'var(--warning-fg, var(--danger-fg))',
      border: '1px solid var(--warning-line, var(--danger-line))',
    },
    info: {
      background: 'var(--primary-bg)',
      color: 'var(--steel-blue)',
      border: '1px solid var(--border)',
    },
  } as const
  return { ...map[variant], padding: '12px 16px', borderRadius: 8, fontSize: 13 }
}

function downloadCsv(contents: string, filename: string): void {
  const blob = new Blob([contents], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function downloadTemplate(): void {
  // Default template is the entitlement-only shape (the primary use: set each
  // person's entitlement, leaving used/pending as tracked).
  downloadCsv(LEAVE_IMPORT_ENTITLEMENT_ONLY_TEMPLATE_CSV, 'leave-entitlement-template.csv')
}

function downloadFullTemplate(): void {
  // Full template adds Taken/Applied/Balance — for a first-time load that also
  // brings in days already taken from another system.
  downloadCsv(LEAVE_IMPORT_TEMPLATE_CSV, 'leave-balance-full-template.csv')
}

// ── Step 1: Prepare ───────────────────────────────────────────────────────────

interface PrepareStepProps {
  onNext: () => void
}

export function PrepareStep({ onNext }: PrepareStepProps): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--text)' }}>
        Set your employees' leave entitlements from a CSV. Nothing changes until you click{' '}
        <strong>Import</strong>, and you preview everything first.
      </p>

      {/* Column guide */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
          Your CSV needs these columns (in any order, extra columns are fine):
        </div>
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 8,
            overflow: 'hidden',
            fontSize: 13,
          }}
        >
          {(
            [
              { col: 'Year', desc: 'The leave year this balance is for (e.g. 2026)' },
              { col: 'Employee No.', desc: "HR's staff number — used to match the person in Leave Hub" },
              { col: 'Employee Name', desc: 'Full name — used only as a cross-check, not for matching' },
              { col: 'Leave Type', desc: 'The leave type name (e.g. ANNUAL LEAVE)' },
              { col: 'Entitlement', desc: 'The entitlement to set for the year (e.g. 20)' },
              { col: 'Taken', desc: 'Optional — days already taken. Omit to keep what Leave Hub already tracks' },
              { col: 'Applied', desc: 'Optional — days applied but not yet approved. Omit to keep pending as-is' },
              { col: 'Balance', desc: 'Optional — a figure to check the result against (verification only)' },
            ] as const
          ).map(({ col, desc }, i) => (
            <div
              key={col}
              style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr',
                borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                background: i % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)',
              }}
            >
              <div
                style={{
                  padding: '8px 12px',
                  fontFamily: 'var(--font-mono, monospace)',
                  fontWeight: 600,
                  color: 'var(--text)',
                  borderRight: '1px solid var(--border)',
                }}
              >
                {col}
              </div>
              <div style={{ padding: '8px 12px', color: 'var(--text-2)' }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Gotchas callout */}
      <div role="note" style={alertBox('warning')}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>A few things to check before uploading</div>
        <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.6 }}>
          <li>
            <strong>Year must be filled in.</strong> Some old-system exports leave the Year column
            blank. Open the file in Excel and fill in the year for every row before uploading.
          </li>
          <li>
            <strong>Every leave type in your file is imported.</strong> Replacement and Unpaid leave
            have no tracked balance, so they come in at zero available and won't add bookable days. A
            leave-type name Leave Hub doesn't recognise is flagged as an error row in the preview, never
            dropped silently.
          </li>
          <li>
            <strong>Only change entitlement?</strong> Leave out the Taken, Applied and Balance columns.
            Leave Hub will update each person's entitlement (and annual grant) but keep the days they've
            already taken and any pending requests exactly as they are.
          </li>
        </ul>
      </div>

      {/* Template download */}
      <div
        style={{
          padding: '14px 16px',
          background: 'var(--surface-2)',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
            Not sure about the column names?
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            Download the entitlement template — the headers with a sample row. Also
            importing days already taken?{' '}
            <button
              type="button"
              onClick={downloadFullTemplate}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                font: 'inherit',
                color: 'var(--steel-blue)',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Get the full template
            </button>
            .
          </div>
        </div>
        <button type="button" onClick={downloadTemplate} style={secondaryBtn()}>
          Download template
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onNext} style={primaryBtn()}>
          Next: upload your file →
        </button>
      </div>
    </div>
  )
}

// ── Step 2: Upload ────────────────────────────────────────────────────────────

interface UploadStepProps {
  onPreviewReady: (rows: LeaveImportRow[], result: LeaveImportResult) => void
  onBack: () => void
}

export function UploadStep({ onPreviewReady, onBack }: UploadStepProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  const [parseErrors, setParseErrors] = useState<Array<{ line: number; message: string }>>([])
  const [serverError, setServerError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [typeError, setTypeError] = useState<string | null>(null)

  // Both the click-to-choose input and the drop target funnel through here so
  // a non-CSV is rejected with a clear message before we try to parse it.
  function acceptFile(file: File): void {
    const name = file.name.toLowerCase()
    const looksCsv =
      name.endsWith('.csv') || file.type === 'text/csv' || file.type === 'application/vnd.ms-excel'
    if (!looksCsv) {
      setTypeError(`"${file.name}" is not a CSV file. Please use a .csv export.`)
      return
    }
    setTypeError(null)
    handleFile(file)
  }

  function handleFile(file: File): void {
    setParseErrors([])
    setServerError(null)
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = typeof e.target?.result === 'string' ? e.target.result : ''
      const { rows, errors } = parseLeaveCsv(text)

      const structureError = errors.find((err) => err.line === 0)
      if (structureError != null) {
        setParseErrors(errors)
        return
      }

      if (errors.length > 0) {
        setParseErrors(errors)
      }

      if (rows.length === 0) return

      setBusy(true)
      importLeaveBalances(rows, true)
        .then((result) => {
          onPreviewReady(rows, result)
        })
        .catch((err: unknown) => {
          setServerError(err instanceof Error ? err.message : 'Server error — please try again.')
        })
        .finally(() => {
          setBusy(false)
        })
    }
    reader.readAsText(file)
  }

  const hasStructureError = parseErrors.some((e) => e.line === 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 14, color: 'var(--text-2)' }}>
        Drag your CSV file here or click to choose. Leave Hub will check it and show you a preview before
        anything is saved.
      </div>

      <label
        onDragEnter={(e) => {
          e.preventDefault()
          if (!busy) setDragging(true)
        }}
        onDragOver={(e) => {
          e.preventDefault()
          if (!busy && !dragging) setDragging(true)
        }}
        onDragLeave={(e) => {
          // Only clear when the pointer actually leaves the drop zone, not when
          // it crosses onto a child element.
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          if (busy) return
          const file = e.dataTransfer.files?.[0]
          if (file != null) acceptFile(file)
        }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          border: `2px dashed ${dragging ? 'var(--steel-blue)' : 'var(--border)'}`,
          borderRadius: 12,
          padding: '40px 24px',
          textAlign: 'center',
          cursor: busy ? 'not-allowed' : 'pointer',
          background: dragging ? 'var(--primary-bg)' : 'var(--surface)',
          transition: 'background 120ms var(--motion-ease-out), border-color 120ms var(--motion-ease-out)',
        }}
      >
        <span style={{ fontSize: 32 }}>📂</span>
        <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)' }}>
          {busy
            ? 'Checking your file…'
            : dragging
              ? 'Drop your CSV file to upload'
              : 'Drag a CSV file here, or click to choose'}
        </span>
        <span style={{ fontSize: 13, color: 'var(--text-3)' }}>
          Accepts .csv files. Columns can be in any order.
        </span>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          disabled={busy}
          tabIndex={-1}
          style={VISUALLY_HIDDEN}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file != null) acceptFile(file)
            e.target.value = ''
          }}
        />
      </label>

      {typeError != null ? (
        <div role="alert" style={alertBox('danger')}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Unsupported file</div>
          {typeError}
        </div>
      ) : null}

      {hasStructureError ? (
        <div role="alert" style={alertBox('danger')}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            This file is missing required columns
          </div>
          <p style={{ margin: '0 0 6px' }}>{parseErrors[0]?.message}</p>
          <p style={{ margin: 0, color: 'inherit', opacity: 0.85 }}>
            Go back to Step 1 and download the template to see the expected column names.
          </p>
        </div>
      ) : parseErrors.length > 0 ? (
        <div role="alert" style={alertBox('warning')}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {parseErrors.length} row{parseErrors.length === 1 ? '' : 's'} skipped before upload
          </div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {parseErrors.map((err) => (
              <li key={err.line}>
                Line {err.line}: {err.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {serverError != null ? (
        <div role="alert" style={alertBox('danger')}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Could not check the file</div>
          {serverError}
        </div>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <button type="button" onClick={onBack} style={secondaryBtn()} disabled={busy}>
          ← Back
        </button>
      </div>
    </div>
  )
}

// ── Step 3: Preview ───────────────────────────────────────────────────────────

interface PreviewStepProps {
  rows: LeaveImportRow[]
  preview: LeaveImportResult
  onBack: () => void
  onCommit: (result: LeaveImportResult) => void
}

export function PreviewStep({ rows, preview, onBack, onCommit }: PreviewStepProps): JSX.Element {
  const [busy, setBusy] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  // Row indices HR has chosen to skip. Keyed by the dry-run rowIndex, which
  // equals the parsed-rows array index (the route enumerates body.rows).
  const [skipped, setSkipped] = useState<ReadonlySet<number>>(new Set())

  function toggleSkip(rowIndex: number): void {
    setSkipped((prev) => {
      const next = new Set(prev)
      if (next.has(rowIndex)) next.delete(rowIndex)
      else next.add(rowIndex)
      return next
    })
  }

  // Counts reflect the kept set (excluding rows HR has skipped), so the
  // summary + button update live as HR toggles Skip.
  const kept = preview.rows.filter((r) => !skipped.has(r.rowIndex))
  const newCount = kept.filter((r) => r.action === 'create').length
  const updateCount = kept.filter((r) => r.action === 'update').length
  const errorCount = kept.filter((r) => r.action === 'error').length
  const discrepancyCount = kept.filter((r) => r.discrepancy).length
  const skippedCount = skipped.size
  const importCount = newCount + updateCount

  function handleCommit(): void {
    setBusy(true)
    setServerError(null)
    // Post only the rows HR kept (drop skipped indices).
    const kept = rows.filter((_, idx) => !skipped.has(idx))
    importLeaveBalances(kept, false)
      .then((result) => {
        onCommit(result)
      })
      .catch((err: unknown) => {
        setServerError(err instanceof Error ? err.message : 'Import failed — please try again.')
        setBusy(false)
      })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Summary */}
      <div
        style={{
          padding: '14px 16px',
          background: 'var(--surface-2)',
          borderRadius: 8,
          fontSize: 14,
          color: 'var(--text)',
          lineHeight: 1.6,
        }}
      >
        <strong>{newCount}</strong> new
        {newCount === 1 ? ' balance' : ' balances'} will be created,{' '}
        <strong>{updateCount}</strong>{' '}
        {updateCount === 1 ? 'record' : 'records'} will be updated,{' '}
        <strong>{skippedCount}</strong> skipped by you
        {errorCount > 0 ? (
          <>
            , <strong style={{ color: 'var(--danger-fg)' }}>{errorCount}</strong>{' '}
            need{errorCount === 1 ? 's' : ''} your attention
          </>
        ) : null}
        {discrepancyCount > 0 ? (
          <>
            ,{' '}
            <strong style={{ color: 'var(--warning-fg, var(--danger-fg))' }}>
              {discrepancyCount}
            </strong>{' '}
            differ from the Balance in your file.
          </>
        ) : (
          ', every row matches the Balance in your file.'
        )}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-3)' }}>
        {(
          [
            { label: 'New', bg: 'var(--success-bg)', color: 'var(--success-fg)', note: '— will be added' },
            { label: 'Update', bg: 'var(--primary-bg)', color: 'var(--steel-blue)', note: '— existing record will change' },
            { label: 'Error', bg: 'var(--danger-bg)', color: 'var(--danger-fg)', note: '— fix the row before importing' },
          ] as const
        ).map(({ label, bg, color, note }) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span
              style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: bg, color }}
            >
              {label}
            </span>
            <span>{note}</span>
          </span>
        ))}
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: 'var(--warning-fg, var(--danger-fg))' }}>⚠</span>
          <span>— Leave Hub computed a different balance; hover for details</span>
        </span>
      </div>

      {serverError != null ? (
        <div role="alert" style={{ padding: '12px 16px', borderRadius: 8, fontSize: 13, background: 'var(--danger-bg)', color: 'var(--danger-fg)', border: '1px solid var(--danger-line)' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Import failed</div>
          {serverError}
        </div>
      ) : null}

      <LeaveImportPreviewTable rows={preview.rows} skipped={skipped} onToggleSkip={toggleSkip} />

      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <button type="button" onClick={onBack} style={secondaryBtn()} disabled={busy}>
          ← Back
        </button>
        <button
          type="button"
          onClick={handleCommit}
          disabled={busy || importCount === 0}
          style={primaryBtn(busy || importCount === 0)}
        >
          {busy ? 'Importing…' : `Import ${importCount} ${importCount === 1 ? 'row' : 'rows'} now`}
        </button>
      </div>
    </div>
  )
}

// ── Step 4: Done ──────────────────────────────────────────────────────────────

interface DoneStepProps {
  preview: LeaveImportResult
  onReset: () => void
}

export function DoneStep({ preview, onReset }: DoneStepProps): JSX.Element {
  const imported = preview.wouldCreate + preview.wouldUpdate
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div
        style={{
          padding: '20px 24px',
          background: 'var(--success-bg)',
          border: '1px solid var(--success-line)',
          borderRadius: 10,
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--success-fg)', marginBottom: 8 }}>
          Import complete
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-2)' }}>
          Imported {imported} leave balance{imported === 1 ? '' : 's'} into Leave Hub
          ({preview.wouldCreate} new, {preview.wouldUpdate} updated).
          {preview.errorCount > 0
            ? ` ${preview.errorCount} row${preview.errorCount === 1 ? '' : 's'} were not imported (had errors).`
            : ''}
        </div>
      </div>

      <div
        role="note"
        style={{
          fontSize: 13,
          color: 'var(--text-2)',
          padding: '12px 16px',
          background: 'var(--surface-2)',
          borderRadius: 8,
        }}
      >
        To confirm, open any employee's profile and check their leave balances tab. The imported
        balances appear alongside their regular Leave Hub accruals.
      </div>

      <div>
        <button type="button" onClick={onReset} style={secondaryBtn()}>
          Import another file
        </button>
      </div>
    </div>
  )
}
